import type { DocumentEntry } from "@school-sales-ai/shared";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { useDeleteDocumentMutation } from "@/hooks/useDocuments";

type Props = {
  doc: DocumentEntry | null;
  onClose: () => void;
};

/**
 * Confirms a permanent delete. The copy points at "Desactivar" on purpose: most
 * admins who reach for delete only want the agent to stop sending a file, and
 * that one is reversible.
 */
export function DeleteDocumentDialog({ doc, onClose }: Props): React.JSX.Element {
  const deleteMutation = useDeleteDocumentMutation();

  function handleConfirm(): void {
    if (!doc) return;
    deleteMutation.mutate(doc.id, { onSuccess: () => onClose() });
  }

  return (
    <AlertDialog open={doc !== null} onOpenChange={(v: boolean) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar «{doc?.title}»?</AlertDialogTitle>
          <AlertDialogDescription>
            Se borra el archivo y no se puede deshacer. Si solo quieres que el agente deje
            de enviarlo, desactívalo.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={deleteMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleteMutation.isPending ? "Eliminando…" : "Eliminar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
